import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { SignalIcon } from '@heroicons/react/24/solid';
import { useFeatureFlagsContext } from '../../../../providers/FeatureFlagsProvider/FeatureFlags.context';
import { ChatEnum, ChatState } from '../../Chat.enum';
import { useModalsContext } from '../../../../providers/ModalsProvider/Modals.context';
import {
  eChannelType,
  isSLBSupportedChannel,
} from '../../../../providers/SocketsProvider/Sockets.enums';
import { eIntegrationStatus } from '../../../../providers/IntegrationsProvider/Integrations.enums.ts';
import { useSubscriptionContext } from '../../../../providers/SubscriptionProvider/Subscription.context.ts';
import { useToastContext } from '../../../../providers/ToastProvider/Toast.hook.ts';
import { eAlertType } from '../../../../providers/ToastProvider/Toast.enum.ts';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook.ts';
import { eUserRole } from '../../../../providers/UserProvider/User.enums.ts';
import { isTikTokChannel } from '../../../../constants/channel.constants';
import { useChatContext } from '../../Chat.hook';
import { useBoardSlice } from 'src/pages/Pipe/PipeBoard.context';
import AllowTypesBanner from './components/AllowTypesBanner/AllowTypesBanner.component';
import Assets from './components/Assets/Assets.component';
import NoConnectionBanner from './components/NoConnectionBanner/NoConnectionBanner.component';
import { tChannelDisplayName } from './components/NoConnectionBanner/NoConnectionBanner.types';
import {
  selectDisconnectedBannerConfig,
  tDisconnectedBannerConfig,
} from './components/NoConnectionBanner/NoConnectionBanner.functions';
import PlusMenu from './components/PlusMenu/PlusMenu.component';
import { RenderActionComponent } from './components/RenderAction/RenderAction.component';
import { ReplyAction } from './components/ReplyAction/ReplyAction.component';
import TextAreaContainer from './components/TextAreaContainer/TextAreaContainer.component';
import EmojiAction from './components/ToolActions/components/EmojiAction/EmojiAction.component';
import useAutoRespondToggle from './components/ToolActions/components/AutoRespondToggle/AutoRespondToggle.hook';
import HandoffReasonModal from './components/ToolActions/components/AutoRespondToggle/components/HandoffReasonModal/HandoffReasonModal.component';
import ToolActions from './components/ToolActions/ToolActions.component';
import WhatsAppWebReadOnlyBanner from './components/WhatsAppWebReadOnlyBanner/WhatsAppWebReadOnlyBanner.component';
import BlockedContactBanner from './components/BlockedContactBanner/BlockedContactBanner.component';
import WapiPlanReadOnlyBanner from './components/WapiPlanReadOnlyBanner/WapiPlanReadOnlyBanner.component';
import { tAdvanceSearchLead } from '../../../AdvanceSearch/AdvanceSearchModal.types';
import { isWhatsAppWebChannelReadOnly } from '../../useWhatsAppWebConnection.hook';
import useContactBlock from '../../useContactBlock.hook';
import { useWhatsAppWebDisconnectReason } from 'src/hooks/whatsapp/useWhatsAppWebDisconnectReason.hook';
import { resolveWhatsAppDisconnectLabel } from 'src/hooks/whatsapp/whatsappDisconnectLabels.constants';
import {
  formatPlanPauseDate,
  isOutboundMessagingBlocked,
} from 'src/functions/planPause.functions';
import { useMessageBarContext } from './MessageBar.hook';
import {
  COMMERCIAL_PAUSE_REASONS,
  isLeadAgentPausedBannerDismissed,
  storeLeadAgentPausedBannerDismissal,
} from './MessageBar.pausedBannerDismissal';
import type { tUseLeadAgentChatBanner } from './useLeadAgentChatBanner.hook';
import LeadAgentPauseDismissDialog from './components/LeadAgentPauseDismissDialog/LeadAgentPauseDismissDialog.component';
import { useIntegrationsContext } from '../../../../providers/IntegrationsProvider/Integrations.context.ts';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tooltip } from 'react-daisyui';
import { Button as DesignSystemButton } from '@leadsales/design-system/react';
import { PauseIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { useUserContext } from '../../../../providers/UserProvider/User.context.ts';
import { useWorkspaceContext } from '../../../../providers/WorkspaceProvider/Workspace.context.ts';
import LeadbotMsgIcon from '../../../../assets/Leadboot/leadbot-reactivated-msg.svg';
import { eLeadbotState } from '../../../../pages/MessengerV2/Messenger.enum.ts';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context.ts';
import {
  tChatStatusUpdatedEvent,
  tSlbEscalatedEvent,
  tSlbSessionExpiredEvent,
  tSlbSessionGateFailedEvent,
  tSlbSessionStateChangedEvent,
} from 'src/providers/SocketsProvider/Sockets.types.ts';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context.ts';
import { eWhatsappApiWindow } from 'src/pages/WhatsappApi/WhatsappApi.enum.ts';
import { eTemplatesTable } from 'src/pages/WhatsappApi/components/TemplatesTable/TemplatesTable.enum.ts';
import { RouterPaths } from 'src/components/Router/Router.constants.ts';
import { SettingsTabs } from 'src/pages/Settings/Settings.enum';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { useWalletFlag } from 'src/pages/Wallet/hooks/useWalletFlag';
import { useWalletSurfaceAccess } from 'src/pages/Wallet/hooks/useWalletSurfaceAccess';
import SLBDirectorPanel from '../SLBDirectorPanel/SLBDirectorPanel.component';
import UpcomingSendsBar from '../UpcomingSendsBar/UpcomingSendsBar.component';
import { useComposerServiceSeal } from 'src/pages/Wallet/hooks/useComposerServiceSeal';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import {
  LEAD_AGENT_PAYWALL_EVENTS,
  type tLeadAgentChatPausedViewedProps,
} from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';
import ReactivateLeadAgentModal from 'src/components/LeadAgentAcceptanceBanner/modals/ReactivateLeadAgentModal/ReactivateLeadAgentModal.component';
import type { tReactivateLeadAgentModalSuccess } from 'src/components/LeadAgentAcceptanceBanner/modals/ReactivateLeadAgentModal/ReactivateLeadAgentModal.types';
import UpgradeTierModal from 'src/components/LeadAgentAcceptanceBanner/modals/UpgradeTierModal/UpgradeTierModal.component';
import { formatPaymentDate } from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';
import { useLeadAgentTrialCapActions } from 'src/components/LeadAgentTrialCapBanner/LeadAgentTrialCapActions.hook';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import type {
  tLeadAgentReadStatus,
  tLeadAgentUnlockOffer,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import { isBillingGateName } from '../../Chat.functions';
import { PLAN_PAUSE_SUBSCRIPTION_TARGET } from 'src/alerts/AlertCenter.constants';
import { isConvertedProOrAdvanced } from 'src/providers/SubscriptionProvider/Subscription.utils';

// Module-level set survives component unmount/remount (modal close/open).
// Reset on full page navigation (expected — fresh session = fresh context).
const slbBannerDismissedChats = new Set<string>();

// Copy for the info banner that appears when an SLB session is deactivated.
// Keyed by the reason the backend stamps on the state.changed event. Falls back
// to the default message when the reason is absent, unknown, or when the
// payload-specific variant is missing prerequisites (e.g. stage_name for
// stage_change).
function resolveDeactivationBannerMessage(
  event: tSlbSessionStateChangedEvent,
  t: TFunction,
): string {
  if (event.reason === 'stage_change' && event.stage_name) {
    return t('smartLeadbot.banners.deactivation.stageChangeWithName', {
      stage_name: event.stage_name,
    });
  }
  const keyByReason: Record<string, string> = {
    stage_change: 'smartLeadbot.banners.deactivation.stageChange',
    manual_toggle: 'smartLeadbot.banners.deactivation.manualToggle',
  };
  return t(
    keyByReason[event.reason ?? ''] ??
      'smartLeadbot.banners.deactivation.default',
  );
}

// What the billing-paused banner says and offers. `noPlan` buys a plan (the
// la-expired reactivation), `capReached` upgrades the tier (the la-usage-capped
// checkout), and `generic` sends the Owner to the subscription settings, as does
// `planIneligible`: a plan that cannot buy the Lead Agent add-on has no checkout.
// Which of the two applies needs the plan, so until the subscription summary
// arrives `planLoading` offers nothing yet and `planUnknown` retries the load.
// A cap can be a trial cap, which has no tier to upgrade: its Owner unlocks the
// trial (`trialCapUnlock`) or, with no self-serve unlock left, contacts support
// (`trialCapSupport`), and its Admin is told to ask the Owner (`trialCapAdmin`).
// Until the usage and offer reads say which cap it is, `capLoading` and
// `trialCapLoading` offer nothing yet and `capUnknown` retries the reads. A
// session that does not read usage needs the subscription summary instead,
// and `capSummaryUnknown` retries that load.
type tBillingPauseKind =
  | 'noPlan'
  | 'planIneligible'
  | 'planLoading'
  | 'planUnknown'
  | 'capReached'
  | 'capLoading'
  | 'capUnknown'
  | 'capSummaryUnknown'
  | 'trialCapUnlock'
  | 'trialCapSupport'
  | 'trialCapAdmin'
  | 'trialCapLoading'
  | 'generic';

type tNoPlanKindOptions = {
  hasSubscriptionData: boolean;
  isPlanEligible: boolean;
  isConfirmedNonClient: boolean;
  isPlanLoading: boolean;
};

type tCapReachedKindOptions = {
  canChooseLeadAgentPlan: boolean;
  isOwner: boolean;
  hasSubscriptionData: boolean;
  isConfirmedNonClient: boolean;
  isPlanLoading: boolean;
  usageReadStatus: tLeadAgentReadStatus;
  usageIsTrialCap: boolean;
  unlockOfferReadStatus: tLeadAgentReadStatus;
  trialCapOffer: tLeadAgentUnlockOffer | null;
  hasUnlockedTrialCapThisSession: boolean;
};

type tBillingPauseKindOptions = {
  reason: string | null;
  noPlanKind: tBillingPauseKind;
  capReachedKind: tBillingPauseKind;
};

// `sellerHint` is the line for a user the banner gives no button: a role that
// cannot choose a plan, or a kind whose `cta` is null. Kinds only Owner/Admin
// reach with a button carry none.
const BILLING_PAUSE_COPY_KEYS: Record<
  tBillingPauseKind,
  { body: string; sellerHint?: string; cta: string | null }
> = {
  noPlan: {
    body: 'smartLeadbot.banners.billingPaused.noPlan',
    sellerHint: 'smartLeadbot.banners.billingPaused.sellerHint',
    cta: 'smartLeadbot.banners.billingPaused.ctaChoosePlan',
  },
  planIneligible: {
    body: 'smartLeadbot.banners.billingPaused.planIneligible',
    sellerHint: 'smartLeadbot.banners.billingPaused.sellerHintGeneric',
    cta: 'smartLeadbot.banners.billingPaused.ctaReviewSubscription',
  },
  planLoading: {
    body: 'smartLeadbot.banners.billingPaused.noPlan',
    sellerHint: 'smartLeadbot.banners.billingPaused.sellerHint',
    cta: 'smartLeadbot.banners.billingPaused.ctaChoosePlan',
  },
  planUnknown: {
    body: 'smartLeadbot.banners.billingPaused.planUnknown',
    sellerHint: 'smartLeadbot.banners.billingPaused.sellerHintGeneric',
    cta: 'smartLeadbot.banners.billingPaused.ctaRetry',
  },
  capReached: {
    body: 'smartLeadbot.banners.billingPaused.capReached',
    sellerHint: 'smartLeadbot.banners.billingPaused.sellerHintCapReached',
    cta: 'smartLeadbot.banners.billingPaused.ctaUpgradePlan',
  },
  capLoading: {
    body: 'smartLeadbot.banners.billingPaused.capReached',
    cta: 'smartLeadbot.banners.billingPaused.ctaLoading',
  },
  capUnknown: {
    body: 'smartLeadbot.banners.billingPaused.capReadFailed',
    cta: 'smartLeadbot.banners.billingPaused.ctaRetry',
  },
  capSummaryUnknown: {
    body: 'smartLeadbot.banners.billingPaused.capReadFailed',
    cta: 'smartLeadbot.banners.billingPaused.ctaRetry',
  },
  trialCapUnlock: {
    body: 'smartLeadbot.banners.billingPaused.trialCapReached',
    cta: 'smartLeadbot.banners.billingPaused.ctaUnlockNow',
  },
  trialCapSupport: {
    body: 'smartLeadbot.banners.billingPaused.trialCapSupport',
    cta: 'smartLeadbot.banners.billingPaused.ctaContactSupport',
  },
  trialCapAdmin: {
    body: 'smartLeadbot.banners.billingPaused.trialCapReached',
    sellerHint: 'smartLeadbot.banners.billingPaused.trialCapAdminHint',
    cta: null,
  },
  trialCapLoading: {
    body: 'smartLeadbot.banners.billingPaused.trialCapReached',
    cta: 'smartLeadbot.banners.billingPaused.ctaLoading',
  },
  generic: {
    body: 'smartLeadbot.banners.billingPaused.generic',
    sellerHint: 'smartLeadbot.banners.billingPaused.sellerHintGeneric',
    cta: 'smartLeadbot.banners.billingPaused.ctaReviewSubscription',
  },
};

// The trial-cap kinds that show the trial end date once the offer read has
// loaded. A failed or pending read shows only its cause and its button.
const TRIAL_CAP_END_DATE_KINDS: ReadonlySet<tBillingPauseKind> = new Set([
  'trialCapUnlock',
  'trialCapSupport',
  'trialCapAdmin',
]);

// The pause reasons that mean no Lead Agent plan. Their banner is the only one
// that can be closed; every other reason keeps showing.
const DISMISSIBLE_PAUSE_REASONS = COMMERCIAL_PAUSE_REASONS;

// A chat context without the workspace close (a provider that predates it)
// reads as not closed, which shows the notice as before.
const CHAT_BANNER_NOT_DISMISSED: tUseLeadAgentChatBanner = {
  dismissed: false,
  markDismissed: () => {},
};

// After a purchase the paused state is read again right away and then at these
// delays; the webhook that lifts the pause lags the checkout. The processing
// copy ends one interval after the last read if none answered allowed.
const PURCHASE_REFETCH_DELAYS_MS = [10_000, 20_000];
const PURCHASE_PROCESSING_MS = 30_000;

/**
 * The banner kind for a workspace without a Lead Agent plan. The subscription's
 * default values read as an ineligible plan, so the plan counts only once real
 * summary data has arrived. An inactive workspace never fetches the summary, so
 * missing data there is not a failed load.
 * @param {tNoPlanKindOptions} options - What is known about the plan.
 * @returns {tBillingPauseKind} The kind that selects copy and call to action.
 */
function resolveNoPlanKind(options: tNoPlanKindOptions): tBillingPauseKind {
  if (options.hasSubscriptionData) {
    return options.isPlanEligible ? 'noPlan' : 'planIneligible';
  }
  if (options.isConfirmedNonClient) return 'planIneligible';
  if (options.isPlanLoading) return 'planLoading';
  return 'planUnknown';
}

/**
 * The banner kind for a cap-reached pause. A trial cap has no tier to upgrade,
 * so the upgrade is offered only once the usage read says the cap is not a
 * trial cap, or when usage is not read for this session and the subscription
 * has loaded or the workspace is inactive (it never fetches the summary). The
 * trial unlock needs the offer, and the extension it buys can be bought once,
 * so after an unlock this session an offer that still says capped (the
 * backend lags) refers the Owner to support.
 * @param {tCapReachedKindOptions} options - The role and what the reads know.
 * @returns {tBillingPauseKind} The kind that selects copy and call to action.
 */
function resolveCapReachedKind(
  options: tCapReachedKindOptions,
): tBillingPauseKind {
  if (!options.canChooseLeadAgentPlan) return 'capReached';
  if (options.usageReadStatus === 'idle') {
    if (options.hasSubscriptionData || options.isConfirmedNonClient) {
      return 'capReached';
    }
    return options.isPlanLoading ? 'capLoading' : 'capSummaryUnknown';
  }
  if (options.usageReadStatus === 'loading') return 'capLoading';
  if (options.usageReadStatus === 'failed') return 'capUnknown';
  if (!options.usageIsTrialCap) return 'capReached';
  if (options.unlockOfferReadStatus === 'failed') return 'capUnknown';
  if (options.unlockOfferReadStatus !== 'loaded') return 'trialCapLoading';
  if (!options.isOwner) return 'trialCapAdmin';
  const offer = options.trialCapOffer;
  return offer?.eligible &&
    offer.phase === 'capped' &&
    !options.hasUnlockedTrialCapThisSession
    ? 'trialCapUnlock'
    : 'trialCapSupport';
}

/**
 * The banner kind for a pause reason. The reason set is open, so any reason
 * not listed is generic.
 * @param {tBillingPauseKindOptions} options - The workspace's pause reason, and the kinds for the reasons that mean no Lead Agent plan and a reached cap.
 * @returns {tBillingPauseKind} The kind that selects copy and call to action.
 */
function resolveBillingPauseKind(
  options: tBillingPauseKindOptions,
): tBillingPauseKind {
  switch (options.reason) {
    case 'acceptance_lapsed':
    case 'no_active_tier':
      return options.noPlanKind;
    case 'cap_reached':
      return options.capReachedKind;
    default:
      return 'generic';
  }
}

export default function MessageBarContent() {
  const { selectedAction, setSelectedAction, inputBlockError } =
    useMessageBarContext();
  const { t, i18n } = useTranslation('chat/chat');
  const navigate = useNavigate();
  const { trackWapiEvent } = useWapiAnalytics();
  const { trackEvent } = useAnalytics();
  const {
    markResolved,
    usageTier,
    usageCapacityPct,
    usageResetDate,
    usageReadStatus,
    usageIsTrialCap,
    unlockOfferReadStatus,
    trialCapOffer,
    retryTrialCapReads,
    hasUnlockedTrialCapThisSession,
    everUsed,
  } = useLeadAgentAcceptanceContext();
  const {
    selectedChat,
    setSelectedChat,
    replyMessage,
    setReplyMessage,
    isResending,
    setIsResending,
    setResendIds,
    whatsAppWebConnection,
    templatePickerRequest,
    setTemplatePickerRequest,
    takeConversationRequest,
    setTakeConversationRequest,
    composerSealQuoteRef,
    setComposerWalletBlocked,
    leadAgentPaused: workspaceLeadAgentPaused,
    refetchLeadAgentPaused,
    leadAgentChatBanner = CHAT_BANNER_NOT_DISMISSED,
  } = useChatContext();
  const {
    selectedLeadForChatModal,
    setSelectedLeadForChatModal,
    setConnectionModalIsOpen,
    setChatModalIsOpen,
    setShowSubscribeIntegrationsModal,
    setMetaConnectionModalIsOpen,
    setWhatsAppBusinessConnectionModalIsOpen,
  } = useModalsContext();
  const {
    hasWaBusinessIntegration,
    hasWaWebAccount,
    whatsappIntegration,
    whatsappBusinessIntegration,
    facebookIntegration,
    instagramIntegration,
    isHydrated: isIntegrationsHydrated,
  } = useIntegrationsContext();
  // BUG-868: paywalls gate on the confirmed non-client signal so a missing or
  // failed summary fetch can't paywall a paying customer.
  const {
    isKnownNonClient,
    subscription,
    hasSubscriptionData,
    isLoadingSubscription,
    isConfirmedNonClient,
    getSummary,
  } = useSubscriptionContext();
  const { addAlert } = useToastContext();
  const { canUserAccessWhatsAppWebQr } = useACLContext();
  const { currentUserRole, currentUserId } = useUserContext();
  const currentUserIsSeller = currentUserRole === eUserRole.SELLER;
  const currentUserIsOwner = currentUserRole === eUserRole.OWNER;
  const planPause = subscription.planPause ?? null;
  const isInPlanPauseWindow = isOutboundMessagingBlocked(subscription);
  const currentUserIsOwnerOrAdmin =
    currentUserRole === eUserRole.OWNER || currentUserRole === eUserRole.ADMIN;
  const currentUserCanReconnectWabi = currentUserIsOwnerOrAdmin;
  const currentUserCanChooseLeadAgentPlan = currentUserIsOwnerOrAdmin;
  const { currentWorkspaceId, isLoadingWorkspaceInfo } = useWorkspaceContext();
  const whatsAppWebDisconnectReason = useWhatsAppWebDisconnectReason({
    workspaceId: currentWorkspaceId,
  });

  const {
    chatStatusUpdatedEvent,
    slbSessionGateFailedEvent,
    slbSessionExpiredEvent,
    slbEscalatedEvent,
    slbSessionStateChangedEvent,
  } = useSocketsContext();
  const {
    slbEscalatedLeadIds,
    slbAcknowledgedLeadIds,
    acknowledgeSlbEscalatedLead,
  } = useBoardSlice();
  const {
    getAndParseTemplates,
    templates,
    templatesHydrated,
    setWhatsappApiWindow,
  } = useWhatsappApiContext();
  const { isSmartLeadBotEnabled } = useFeatureFlagsContext();
  // RET-2254: 24h banner's approved-template copy is a claim about MONEY, so
  // it gates on hasLoadedWalletFlag (settled for THIS workspace), never on
  // isLoadingWalletFlag — that flag reads false for one render with the
  // previous workspace's map still on screen (see useWalletFlag's docblock),
  // which would assert the wrong cohort's copy in that render.
  const { isWalletEnabled, hasLoadedWalletFlag } = useWalletFlag();
  // RET-2339: the two DISPLAY decisions, from the one predicate the wallet
  // surfaces share. `isWalletSurfaceVisible` gates what only informs about
  // money (the seal, `textWallet`); `isKnownWithoutWapiAccess` is positive
  // evidence that this workspace's plan has no WhatsApp API. Neither feeds
  // the send guard (`useComposerServiceSeal`, D1).
  const { isWalletSurfaceVisible, isKnownWithoutWapiAccess } =
    useWalletSurfaceAccess();
  const {
    enabled: aiAutoRespondEnabled,
    isSaving: autoRespondSaving,
    handleToggle: onAutoRespondToggle,
    disabledReason: autoRespondDisabledReason,
    deactivationReason,
    isDeactivationReasonLoaded,
    handoffReasonModalProps,
  } = useAutoRespondToggle();
  const [slbOverrideWrite, setSlbOverrideWrite] = useState(false);
  const [slbDirectorOpen, setSlbDirectorOpen] = useState(false);

  const isSLBChat = isSLBSupportedChannel(selectedChat?.integration);

  // RET-2232 D-30 (vuelta 5, docs/loop/RET-2232-estado.md): el hook nace
  // AQUÍ, no en `TextAreaContainer` (D-1/D-2 originales). Este componente
  // monta LOS DOS que el veredicto tiene que gobernar —
  // `<TextAreaContainer/>` en sus tres ramas (más abajo) y
  // `<ToolActions/>` → `<SendAction/>`, su HERMANO— y está montado en TODAS
  // las ramas del árbol de Lead Agent, incluida la que NO monta el
  // compositor (Lead Agent activo, arriba: sólo banner). Tres vueltas de
  // este ticket encontraron siete caminos por los que un envío salía sin que
  // el saldo alcanzara; los últimos dos compartían una sola causa: el
  // escritor de `isComposerWalletBlocked` vivía dentro de
  // `TextAreaContainer`, un componente que a veces no se monta, mientras
  // `SendAction` (quien lo lee) siempre está. Calcular el veredicto aquí lo
  // cierra por construcción — el hook corre en cada cambio de `selectedChat`
  // sin depender de qué rama decide pintar el textarea, así que el guardia
  // nunca arranca en su `false` inicial ni queda trabado en `true` al pasar
  // de un chat bloqueado a uno con el Lead Agent activo.
  //
  // `TextAreaContainer` recibe `{ seal, isBlocked, costLabel, shortfall }`
  // como props en sus tres sitios de render, y sigue siendo quien pinta la
  // barra y el sello, y quien sincroniza `blockTextArea` (la bandera VISUAL
  // del propio `<Textarea/>`, que no existe en la rama del Lead Agent — nada
  // que proteger ahí).
  //
  // Alias en el destructuring: este archivo ya usa `isBlocked` para el
  // bloqueo de CONTACTO (`useContactBlock`, más abajo) — son dos preguntas
  // distintas y no pueden compartir el nombre.
  const {
    seal: composerSeal,
    sealTooltip: composerSealTooltip,
    isBlocked: isComposerSealBlocked,
    costLabel: composerCostLabel,
    shortfall: composerShortfall,
  } = useComposerServiceSeal({
    selectedChat,
    recipientHandle: selectedLeadForChatModal?.handle,
    recipientName: selectedLeadForChatModal?.name,
    quoteScopeRef: composerSealQuoteRef,
  });

  // Único escritor de `isComposerWalletBlocked` (D-16, reubicado por D-30):
  // recalcula en cada cambio de `selectedChat` sin importar si
  // `TextAreaContainer` está montado, así que `SendAction` siempre lee el
  // veredicto de la conversación ACTUAL, nunca el de la anterior.
  useEffect(() => {
    setComposerWalletBlocked?.(isComposerSealBlocked);
  }, [isComposerSealBlocked, setComposerWalletBlocked]);

  // RET-2339 U4 (D1): the seal is shown only with WhatsApp API access. It is
  // hidden HERE, where it is passed, never inside the hook: the hook's
  // `isBlocked` above keeps reading the cohort flag and keeps failing closed,
  // so the no-balance bar and the send guard behave as before. Accepted cost:
  // a Pro does not see the seal while its plan loads.
  const visibleComposerSeal = isWalletSurfaceVisible ? composerSeal : null;

  useEffect(() => {
    getAndParseTemplates();
  }, [whatsappBusinessIntegration.state]);

  // Intervention is per-chat: leaving the chat exits the mode, otherwise the
  // open composer + paused banner leak into whatever chat is selected next.
  useEffect(() => {
    setSlbOverrideWrite(false);
    setSlbDirectorOpen(false);
  }, [selectedChat?.id]);

  // Reset manual override and director panel whenever the chat changes.
  // NOTE: slbBannerInfo is NOT reset here — it's restored from deactivationReason
  // below. Resetting on aiAutoRespondEnabled would wipe the restored banner because
  // the enabled state arrives async after the deactivation reason.
  useEffect(() => {
    setSlbOverrideWrite(false);
    setSlbDirectorOpen(false);
  }, [selectedChat?.id]);

  // Reset banner only on chat change, not on aiAutoRespondEnabled change.
  useEffect(() => {
    setSlbBannerInfo(null);
  }, [selectedChat?.id]);

  // SLB contextual banner: gate failures, escalation, expiry, and stage changes shown per-chat
  const [slbBannerInfo, setSlbBannerInfo] = useState<{
    message: string;
    detail?: string;
    type: 'warning' | 'info' | 'escalation';
  } | null>(null);

  // RET-2339 (D5) / RET-2349 (R2) — a WhatsApp API chat in a workspace whose
  // plan has no WhatsApp API: history only, no reply. Only the RESOLVED channel
  // (`selectedChat.integration`, what `Chat.hook` getOrCreateChatData took from
  // the chat lookup) decides, never the REQUESTED one
  // (`selectedLeadForChatModal.chat.integration`). «Responder por WhatsApp Web»
  // changes only the requested channel, and the lookup by handle can still
  // return the API chat; if the requested channel were also required, the
  // legend would disappear and the composer would mount over a chat that is
  // still WhatsApp API. In the BUG-336 fallback the chat resolves to WhatsApp
  // Web, so there is no legend and it can be answered there. While the chat is
  // still resolving (`selectedChat` null) there is no legend: nothing can be
  // sent without a chat id, and an unresolved state behaves as today. The
  // verdict is positive evidence only (`isKnownWithoutWapiAccess`).
  const isWapiPlanReadOnly =
    isKnownWithoutWapiAccess &&
    selectedChat?.integration === eChannelType.WHATSAPP_API;
  // The user already asked for WhatsApp Web and the chat still resolved to
  // WhatsApp API: the legend must not offer the same switch again.
  const isWapiPlanWebSwitchFailed =
    selectedLeadForChatModal?.chat?.integration === eChannelType.WHATSAPP_WEB;

  // In-conversation "Lead Agent paused by billing" banner. The workspace-level
  // paused state and its reason come from the chat context (see useLeadAgentPaused).
  // Shown only where the Lead Agent would otherwise be active — an SLB chat with no
  // operational deactivation reason (handoff/manual/window/stage own their own
  // banners), and hidden while another SLB banner is on screen so only one shows.
  // The copy says why by reason; Owner/Admin get the action that fixes it (see
  // tBillingPauseKind), and everyone else is told what to ask the account owner.
  // After a purchase it says the payment is processing until the pause lifts.
  const { paused: leadAgentPaused, reason: leadAgentPausedReason } =
    workspaceLeadAgentPaused;
  // RET-2339: where the plan read-only legend is drawn (it loses only to the
  // plan pause branch), it replaces this banner, so one notice shows. The
  // legend carries its own CTAs: «Responder por WhatsApp Web» when WhatsApp
  // Web is connected, «Mejorar plan» for Owner/Admin. The header chip and the
  // thinking indicator still read the paused state.
  const isWapiPlanLegendShown = isWapiPlanReadOnly && !isInPlanPauseWindow;
  // The summary fetch waits for the workspace info, and the summary's loading
  // flag is already off while that info loads.
  const isSubscriptionSummaryLoading =
    isLoadingSubscription || isLoadingWorkspaceInfo;
  const billingPauseKind = resolveBillingPauseKind({
    reason: leadAgentPausedReason,
    noPlanKind: resolveNoPlanKind({
      hasSubscriptionData,
      isPlanEligible: isConvertedProOrAdvanced(subscription),
      isConfirmedNonClient,
      isPlanLoading: isSubscriptionSummaryLoading,
    }),
    capReachedKind: resolveCapReachedKind({
      canChooseLeadAgentPlan: currentUserCanChooseLeadAgentPlan,
      isOwner: currentUserIsOwner,
      hasSubscriptionData,
      isConfirmedNonClient,
      isPlanLoading: isSubscriptionSummaryLoading,
      usageReadStatus,
      usageIsTrialCap,
      unlockOfferReadStatus,
      trialCapOffer,
      hasUnlockedTrialCapThisSession,
    }),
  });
  const billingPauseCopyKeys = BILLING_PAUSE_COPY_KEYS[billingPauseKind];
  const isBillingPauseCtaLoading =
    billingPauseKind === 'planLoading' ||
    billingPauseKind === 'capLoading' ||
    billingPauseKind === 'trialCapLoading';
  const trialCapEndDate =
    TRIAL_CAP_END_DATE_KINDS.has(billingPauseKind) &&
    unlockOfferReadStatus === 'loaded'
      ? formatPaymentDate(trialCapOffer?.trialEnd ?? null)
      : null;
  const [isReactivateLeadAgentOpen, setIsReactivateLeadAgentOpen] =
    useState(false);
  const [isUpgradeTierOpen, setIsUpgradeTierOpen] = useState(false);
  const [isPurchaseProcessing, setIsPurchaseProcessing] = useState(false);
  // A dismissal is keyed by workspace and user, so it needs both ids: no key
  // is read or written, and no close button shows, until they are known. The
  // stored value is read on every render, so a workspace or user switch reads
  // its own key.
  const pausedBannerDismissalKey =
    currentWorkspaceId && currentUserId
      ? `lead_agent_paused_banner_dismissed_${currentWorkspaceId}_${currentUserId}`
      : null;
  const [lastDismissedBannerKey, setLastDismissedBannerKey] = useState<
    string | null
  >(null);
  // The close in this browser is the Admin's (AGI-777). The Owner's is the
  // workspace's (AGI-766): an Owner who closed it here before AGI-766 sees it
  // again, so they can close it for the whole team, which otherwise keeps it.
  const isPausedBannerDismissed =
    !currentUserIsOwner &&
    pausedBannerDismissalKey !== null &&
    DISMISSIBLE_PAUSE_REASONS.has(leadAgentPausedReason ?? '') &&
    (lastDismissedBannerKey === pausedBannerDismissalKey ||
      isLeadAgentPausedBannerDismissed(pausedBannerDismissalKey));
  // AGI-766 (MLA-100): the Owner closed it for the whole workspace, so no role
  // sees it, in any browser. Only a commercial pause can be closed this way; a
  // paying customer's pause keeps its banner.
  const isWorkspaceBannerDismissed =
    leadAgentChatBanner.dismissed &&
    DISMISSIBLE_PAUSE_REASONS.has(leadAgentPausedReason ?? '');
  const [isDismissSurveyOpen, setIsDismissSurveyOpen] = useState(false);
  // The processing message confirms a purchase, so a dismissal never hides it.
  const showLeadAgentPausedBanner =
    leadAgentPaused &&
    isSLBChat &&
    !deactivationReason &&
    !slbBannerInfo &&
    !isWapiPlanLegendShown &&
    (!(isPausedBannerDismissed || isWorkspaceBannerDismissed) ||
      isPurchaseProcessing);
  // The close button waits for the plan state: while it loads or failed to
  // load, the banner keeps its cause and its retry.
  const canDismissPausedBanner =
    currentUserCanChooseLeadAgentPlan &&
    pausedBannerDismissalKey !== null &&
    (billingPauseKind === 'noPlan' || billingPauseKind === 'planIneligible') &&
    !isPurchaseProcessing;
  function handleDismissPausedBanner() {
    if (!pausedBannerDismissalKey) return;
    // AGI-766: the Owner closes it for the whole workspace, after an optional
    // question; the Admin keeps the close in this browser (AGI-777).
    if (currentUserIsOwner) {
      setIsDismissSurveyOpen(true);
      return;
    }
    storeLeadAgentPausedBannerDismissal(pausedBannerDismissalKey);
    setLastDismissedBannerKey(pausedBannerDismissalKey);
  }
  // The unlock modal closes on a workspace switch only, so a charge started in
  // one chat survives opening another chat of the same workspace.
  const { onOptionClick, onContactSupport, actionModals } =
    useLeadAgentTrialCapActions();

  // The Owner's unlock settling while this chat is open starts the
  // post-purchase reads below. A chat opened after the unlock offers support
  // instead, since the extension can be bought once.
  const hasOwnerUnlockedTrialCap =
    hasUnlockedTrialCapThisSession && currentUserIsOwner;
  const hadOwnerUnlockedTrialCapRef = useRef(hasOwnerUnlockedTrialCap);
  useEffect(() => {
    if (hasOwnerUnlockedTrialCap && !hadOwnerUnlockedTrialCapRef.current) {
      setIsPurchaseProcessing(true);
    }
    hadOwnerUnlockedTrialCapRef.current = hasOwnerUnlockedTrialCap;
  }, [hasOwnerUnlockedTrialCap]);

  // While processing, the paused state is read now and at each delay; the
  // processing copy ends on its own, and the cleanup drops pending reads.
  useEffect(() => {
    if (!isPurchaseProcessing) return;
    refetchLeadAgentPaused();
    const timers = [
      ...PURCHASE_REFETCH_DELAYS_MS.map((delay) =>
        setTimeout(refetchLeadAgentPaused, delay),
      ),
      setTimeout(() => setIsPurchaseProcessing(false), PURCHASE_PROCESSING_MS),
    ];
    return () => timers.forEach(clearTimeout);
  }, [isPurchaseProcessing]);

  // Leaving the chat or workspace, or the pause lifting, ends the processing.
  useEffect(() => {
    setIsPurchaseProcessing(false);
  }, [selectedChat?.id, currentWorkspaceId, leadAgentPaused]);

  // A checkout or the close question opened from this chat's banner closes when
  // another chat opens.
  useEffect(() => {
    setIsReactivateLeadAgentOpen(false);
    setIsUpgradeTierOpen(false);
    setIsDismissSurveyOpen(false);
  }, [selectedChat?.id]);

  function handleBillingPauseCta() {
    if (isBillingPauseCtaLoading) return;
    if (billingPauseKind === 'capReached') setIsUpgradeTierOpen(true);
    else if (billingPauseKind === 'noPlan') setIsReactivateLeadAgentOpen(true);
    else if (
      billingPauseKind === 'planUnknown' ||
      billingPauseKind === 'capSummaryUnknown'
    )
      void getSummary();
    else if (billingPauseKind === 'capUnknown') retryTrialCapReads();
    else if (billingPauseKind === 'trialCapUnlock') onOptionClick('unlock_now');
    else if (billingPauseKind === 'trialCapSupport') onContactSupport();
    else navigate(PLAN_PAUSE_SUBSCRIPTION_TARGET);
  }

  // One view per chat in which the banner is shown, not one per render. It waits
  // for the chat's session read, whose deactivation reason can still hide it, and
  // for the user's role, which picks the event.
  const pausedBannerTrackedChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Reason and role are read when the effect fires; the ref allows one view per chat.
    if (
      !isDeactivationReasonLoaded ||
      !currentUserRole ||
      !showLeadAgentPausedBanner ||
      !selectedChat?.id ||
      pausedBannerTrackedChatIdRef.current === selectedChat.id
    )
      return;
    pausedBannerTrackedChatIdRef.current = selectedChat.id;
    const props: tLeadAgentChatPausedViewedProps = {
      surface: 'chat',
      pause_reason: leadAgentPausedReason ?? 'unknown',
    };
    trackEvent(
      currentUserCanChooseLeadAgentPlan
        ? LEAD_AGENT_PAYWALL_EVENTS.BANNER_VIEWED
        : LEAD_AGENT_PAYWALL_EVENTS.SELLER_PAUSED_VIEWED,
      props,
    );
  }, [
    showLeadAgentPausedBanner,
    selectedChat?.id,
    isDeactivationReasonLoaded,
    currentUserRole,
  ]);

  function handleLeadAgentReactivated(
    result: tReactivateLeadAgentModalSuccess,
  ) {
    markResolved(result.tier);
    setIsPurchaseProcessing(true);
  }
  // Restore deactivation banner on chat open using the reason from the session endpoint.
  // This ensures banners persist even if the user opens the chat after the real-time event.
  // Skips chats where the user already dismissed the banner (tracked in module-level set).
  useEffect(() => {
    if (!deactivationReason || !selectedChat?.id) return;
    if (slbBannerDismissedChats.has(selectedChat.id)) return;
    const bannerMap: Record<
      string,
      { message: string; type: 'warning' | 'info' | 'escalation' }
    > = {
      escalation: {
        message: t('smartLeadbot.banners.escalation.transferred'),
        type: 'escalation',
      },
      expired: { message: t('smartLeadbot.banners.expired'), type: 'info' },
      deactivated: {
        message: t('smartLeadbot.banners.deactivation.default'),
        type: 'info',
      },
    };
    const banner = bannerMap[deactivationReason];
    if (banner) setSlbBannerInfo(banner);
  }, [deactivationReason, selectedChat?.id]);
  const selectedChatIdRef = useRef(selectedChat?.id);
  selectedChatIdRef.current = selectedChat?.id;

  slbSessionGateFailedEvent.useSubscription(
    (event: tSlbSessionGateFailedEvent) => {
      if (!isSmartLeadBotEnabled || event.chat_id !== selectedChatIdRef.current)
        return;
      // A billing pause is the workspace's paused state, shown by the paused
      // banner; it gets no gate banner of its own.
      if (isBillingGateName(event.gate_name)) return;
      const key = `smartLeadbot.banners.gateFailed.${event.gate_name}`;
      const fallback = t('smartLeadbot.banners.gateFailed.default', {
        gate_name: event.gate_name,
      });
      const message = t(key, { defaultValue: fallback });
      setSlbBannerInfo({ message, type: 'warning' });
    },
  );

  slbSessionExpiredEvent.useSubscription((event: tSlbSessionExpiredEvent) => {
    if (!isSmartLeadBotEnabled || event.chat_id !== selectedChatIdRef.current)
      return;
    setSlbBannerInfo({
      message: t('smartLeadbot.banners.expired'),
      type: 'info',
    });
  });

  slbEscalatedEvent.useSubscription((event: tSlbEscalatedEvent) => {
    if (!isSmartLeadBotEnabled || event.chat_id !== selectedChatIdRef.current)
      return;
    const isMaxTurns = event.handoff_trigger === 'max_turns_reached';
    const message = isMaxTurns
      ? t('smartLeadbot.banners.escalation.maxTurns')
      : t('smartLeadbot.banners.escalation.transferred');
    setSlbBannerInfo({ message, detail: event.agent_note, type: 'escalation' });
  });

  slbSessionStateChangedEvent.useSubscription(
    (event: tSlbSessionStateChangedEvent) => {
      if (!isSmartLeadBotEnabled || event.chat_id !== selectedChatIdRef.current)
        return;
      if (event.enabled) {
        // RET-870 — the bot came back online for this chat. Clear sticky state
        // so the UI tracks the real session lifecycle instead of carrying
        // dismissals and overrides forward indefinitely.
        slbBannerDismissedChats.delete(event.chat_id);
        setSlbOverrideWrite(false);
        return;
      }
      setSlbBannerInfo({
        message: resolveDeactivationBannerMessage(event, t),
        type: 'info',
      });
    },
  );

  const shouldRenderNewInputContainer =
    selectedAction !== ChatEnum.PROGRAMMED_MESSAGES &&
    selectedAction !== ChatEnum.NOTES &&
    selectedAction !== ChatEnum.CREATE_QUICK_ACTION;

  const canSendTemplate24Hrs =
    (selectedLeadForChatModal?.chat?.integration === eChannelType.MESSENGER ||
      selectedLeadForChatModal?.chat?.integration === eChannelType.INSTAGRAM) &&
    selectedChat?.state === ChatState.TEMPLATE;
  const cantSendTemplate24Hrs =
    (selectedLeadForChatModal?.chat?.integration === eChannelType.MESSENGER ||
      selectedLeadForChatModal?.chat?.integration === eChannelType.INSTAGRAM) &&
    selectedChat?.state === ChatState.INACTIVE;

  const whatsAppAPIInactive =
    selectedLeadForChatModal?.chat?.integration === eChannelType.WHATSAPP_API &&
    selectedChat?.state === ChatState.INACTIVE;

  const isTiktokChat = isTikTokChannel(
    selectedLeadForChatModal?.chat?.integration,
  );

  const tiktokInactive =
    isTiktokChat &&
    (selectedChat?.state === ChatState.INACTIVE ||
      selectedChat?.state === ChatState.EXPIRED ||
      selectedChat?.state === ChatState.TEMPLATE);

  const isLeadbotReActivated =
    selectedLeadForChatModal?.leadbotState ===
    eLeadbotState.SESSION_REACTIVATED;

  const isChatActive =
    !selectedChat?.state || selectedChat?.state === ChatState.ACTIVE;

  const isWhatsappWeb =
    selectedLeadForChatModal?.chat?.integration === eChannelType.WHATSAPP_WEB;

  const shouldShowChatInput = isChatActive || isWhatsappWeb;

  // INT-941 / U2 — read-only composer for the WhatsApp Web view of a workspace
  // whose WhatsApp Web account no longer exists. Keyed on the REQUESTED
  // integration (`isWhatsappWeb`, the field the channel dropdown mutates) so it
  // never disagrees with the existing branches mid-toggle. The live
  // `whatsAppWebConnection` status (U1) is the source of truth: `absent` ->
  // read-only; `present` -> editable (coexistence / disconnected); `pending` /
  // `error` -> degrade to the cached IntegrationsProvider hint (never hard
  // fail-closed, so a transient blip cannot lock a healthy sender out).
  const whatsAppWebPhase = whatsAppWebConnection.phase;
  const isWhatsAppWebReadOnly =
    isWhatsappWeb &&
    isWhatsAppWebChannelReadOnly(whatsAppWebPhase, hasWaWebAccount());

  // INT-1294 — blocked WAPI contact: Meta never delivers to a blocked user, so
  // the whole composer (input, toolbar, templates, send) is unmounted and the
  // banner states why the chat box is disabled, with the unblock one click
  // away. Gated on the requested integration so the WhatsApp Web view of the
  // same lead keeps its own composer semantics.
  const {
    isBlocked,
    loading: unblockLoading,
    setContactBlocked,
  } = useContactBlock();
  const isContactBlocked =
    isBlocked &&
    selectedLeadForChatModal?.chat?.integration === eChannelType.WHATSAPP_API;

  // RET-2339 (D6) — allow-list, not deny-list. In a read-only chat only NOTES
  // (internal, never reaches the lead) may stay open. Any other action —
  // inherited from another chat, opened by the Meta notice's template picker,
  // or anything `RenderActionComponent` learns to draw later — is reset to
  // CHAT. Forward selection and a pending reply are dropped too: their panels
  // are not drawn here, so leaving them set would strand the user in a mode
  // with no visible way out.
  useEffect(() => {
    if (!isWapiPlanReadOnly) return;
    if (
      selectedAction !== ChatEnum.CHAT &&
      selectedAction !== ChatEnum.NOTES
    ) {
      setSelectedAction(ChatEnum.CHAT);
    }
    if (isResending) {
      setIsResending(false);
      setResendIds([]);
    }
    if (replyMessage) setReplyMessage(null);
  }, [isWapiPlanReadOnly, selectedAction, isResending, replyMessage]);

  // What the allow-list lets `RenderActionComponent` draw, decided at render
  // time so a disallowed action never paints, not even for the render before
  // the reset effect above runs. Forward selection wins inside
  // `RenderActionComponent`, so it is excluded explicitly.
  const isActionAllowedInReadOnlyChat =
    selectedAction === ChatEnum.NOTES && !isResending;

  // RET-2339 — the read-only chat can still be answered through WhatsApp Web
  // when the workspace has it connected. It reads the same WhatsApp Web state
  // as the `wapiBanner.migratedToWeb` notice and uses the same switch; the
  // notice's other conditions (no WABA, API chat inactive) do not apply here,
  // because this legend only shows on a chat resolved to WhatsApp API. It
  // waits for the integrations, so the button and the view event below never
  // disagree, and the legend does not flip variants while they load.
  const canReplyWapiPlanChatByWeb =
    isIntegrationsHydrated &&
    whatsappIntegration.state === eIntegrationStatus.CONNECTED;

  // One exposure per chat opened in this composer: the ref keeps the last
  // chat id, so re-renders do not repeat it and opening another chat (or the
  // same one again after another) counts again. It waits for the integrations
  // so `has_whatsapp_web` is the settled answer, not the provider default.
  const wapiPlanLegendTrackedChatRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isWapiPlanLegendShown || !isIntegrationsHydrated || !selectedChat?.id)
      return;
    if (wapiPlanLegendTrackedChatRef.current === selectedChat.id) return;
    wapiPlanLegendTrackedChatRef.current = selectedChat.id;
    trackWapiEvent(WAPI_EVENTS.CHAT_PLAN_READ_ONLY_VIEWED, {
      wapi_context: 'chat',
      chat_id: selectedChat.id,
      has_whatsapp_web: canReplyWapiPlanChatByWeb,
    });
  }, [
    isWapiPlanLegendShown,
    isIntegrationsHydrated,
    selectedChat?.id,
    canReplyWapiPlanChatByWeb,
    trackWapiEvent,
  ]);

  function handleWapiPlanReplyByWeb() {
    trackWapiEvent(WAPI_EVENTS.CHAT_PLAN_READ_ONLY_REPLY_BY_WEB_CLICK, {
      wapi_context: 'chat',
      chat_id: selectedChat?.id,
    });
    switchToWhatsAppWeb();
  }

  function handleWapiPlanUpgrade() {
    trackWapiEvent(WAPI_EVENTS.CHAT_PLAN_READ_ONLY_UPGRADE_CLICK, {
      wapi_context: 'chat',
      chat_id: selectedChat?.id,
      has_whatsapp_web: canReplyWapiPlanChatByWeb,
    });
    setChatModalIsOpen(false);
    navigate(
      `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&upgrade=true&cta_type=wapi_chat_read_only`,
    );
  }

  // Retry is offered only when the live check errored and the banner is
  // showing anyway (the cached hint also said no account). A clean `absent`
  // needs no retry.
  const showWhatsAppWebRetry =
    isWhatsAppWebReadOnly && whatsAppWebPhase === 'error';

  // INT-941 / U2 — switch the open chat to the WhatsApp Business view. This is
  // the same mutation as the WhatsApp Business option in LeadInfo's channel
  // dropdown; the read-only banner surfaces it as an explicit button because
  // that dropdown's icon-only trigger is hard to discover.
  const switchToWhatsAppBusiness = () => {
    setSelectedLeadForChatModal(
      (prevValue) =>
        ({
          ...prevValue,
          chat: {
            ...prevValue?.chat,
            integration: eChannelType.WHATSAPP_API,
          },
        }) as tAdvanceSearchLead,
    );
  };

  // BUG-773 — mirrors switchToWhatsAppBusiness above. Offered when a chat is
  // stuck on a dead WHATSAPP_API channel (post plan-downgrade) but the
  // workspace has a live WhatsApp Web account to send through instead.
  const switchToWhatsAppWeb = () => {
    setSelectedLeadForChatModal(
      (prevValue) =>
        ({
          ...prevValue,
          chat: {
            ...prevValue?.chat,
            integration: eChannelType.WHATSAPP_WEB,
          },
        }) as tAdvanceSearchLead,
    );
  };

  const getInactiveBannerText = () => {
    if (selectedChat?.state === ChatState.EXPIRED) {
      return t('page.conversation.expired');
    }
    if (canSendTemplate24Hrs) {
      return t('page.conversation.template');
    }
    if (cantSendTemplate24Hrs) {
      return t('page.conversation.cant-send-template');
    }
    return t('page.conversation.not-active');
  };

  chatStatusUpdatedEvent.useSubscription((event: tChatStatusUpdatedEvent) => {
    setSelectedChat((previous) => {
      if (
        !previous ||
        previous.leadId !== event.lead_id ||
        previous.state === event.chat_state
      ) return previous;

      return { ...previous, state: event.chat_state as ChatState };
    });
  });

  function handleReconnectWhatsapp() {
    if (currentUserIsSeller) {
      addAlert({
        type: eAlertType.INFO,
        message: t('reconnectUnauthorized', { channel: 'WhatsApp Web' }),
      });
      return;
    }
    if (isKnownNonClient) {
      setShowSubscribeIntegrationsModal(true);
      setChatModalIsOpen(false);
      return;
    }
    // Only close the chat modal when we can actually open the QR modal —
    // otherwise the user is left on the Dashboard with no feedback.
    if (!canUserAccessWhatsAppWebQr()) return;
    setConnectionModalIsOpen(true);
    setChatModalIsOpen(false);
  }

  function handleReconnectMeta(channelName: tChannelDisplayName) {
    if (currentUserIsSeller) {
      addAlert({
        type: eAlertType.INFO,
        message: t('reconnectUnauthorized', { channel: channelName }),
      });
      return;
    }
    if (isKnownNonClient) {
      setShowSubscribeIntegrationsModal(true);
      setChatModalIsOpen(false);
      return;
    }
    // MetaConnectionModal is rendered globally by ModalsProvider, so opening
    // it in-place keeps the user in their chat context instead of stranding
    // them on the Dashboard root. Matches the WhatsApp Web pattern above.
    setChatModalIsOpen(false);
    setMetaConnectionModalIsOpen(true);
  }

  function renderDisconnectedBanner() {
    // Suppress the banner until the integrations fetch resolves. The default
    // per-integration state is DISCONNECTED, so without this gate every chat
    // open would flash the banner for the duration of the initial round-trip.
    if (!isIntegrationsHydrated) return null;

    const chatChannel = selectedLeadForChatModal?.chat?.integration;

    const configs: tDisconnectedBannerConfig[] = [
      {
        channel: eChannelType.WHATSAPP_WEB,
        name: 'WhatsApp Web',
        integrationState: whatsappIntegration.state,
        onReconnect: handleReconnectWhatsapp,
        // When WhatsApp Business API is connected, the user has an alternative
        // WA integration, so we surface the disconnected state without a CTA.
        showReconnectButton:
          whatsappBusinessIntegration.state !== eIntegrationStatus.CONNECTED,
        isApplicable: hasWaWebAccount(),
      },
      {
        channel: eChannelType.MESSENGER,
        name: 'Messenger',
        integrationState: facebookIntegration.state,
        onReconnect: () => handleReconnectMeta('Messenger'),
        showReconnectButton: true,
      },
      {
        channel: eChannelType.INSTAGRAM,
        name: 'Instagram',
        integrationState: instagramIntegration.state,
        onReconnect: () => handleReconnectMeta('Instagram'),
        showReconnectButton: true,
      },
    ];

    const match = selectDisconnectedBannerConfig(configs, chatChannel);

    if (!match) return null;

    let disconnectReason: string | null = null;
    if (match.channel === eChannelType.WHATSAPP_WEB) {
      const label = resolveWhatsAppDisconnectLabel(
        whatsAppWebDisconnectReason.errorCode,
        whatsAppWebDisconnectReason.errorMessage,
      );
      if (label?.translationKey) {
        disconnectReason = t(label.translationKey);
      } else if (label?.fallbackText) {
        // Raw provider message — only reached when no error_code arrived
        // (the localized UNKNOWN label is preferred whenever a code exists).
        disconnectReason = label.fallbackText;
      }
    }

    return (
      <NoConnectionBanner
        channelName={match.name}
        onReconnect={match.onReconnect}
        showReconnectButton={match.showReconnectButton}
        disconnectReason={disconnectReason}
      />
    );
  }

  // INT-1271 — the gate is the WORKSPACE'S OWN templates now, not the
  // deprecated system "opening template" wrapper. A workspace with an
  // approved template of its own can use the real picker unchanged; one with
  // only unapproved templates, or none at all, has no legitimate way to
  // message a lead outside the 24h window and gets routed to the Plantillas
  // tab instead of the old generic wrapper (Be Clip: 217 wrappers to 208
  // contacts in a day → Meta SPAM-flagged the number twice in a week).
  const hasApprovedTemplate = templates.some(
    (template) => template.status === eTemplatesTable.APPROVED,
  );
  const hasAnyTemplate = templates.length > 0;

  function handleClickOnOpeningTemplate() {
    if (hasApprovedTemplate) {
      setSelectedAction(ChatEnum.WABTEMPLATES);
      return;
    }

    trackWapiEvent(WAPI_EVENTS.CHAT_NO_APPROVED_TEMPLATE_REDIRECT, {
      reason: hasAnyTemplate ? 'pending_approval' : 'zero_templates',
    });
    setChatModalIsOpen(false);
    setWhatsappApiWindow(eWhatsappApiWindow.VIEW_TEMPLATES);
    navigate(RouterPaths.wapi);
  }

  // INT-1504: the Meta error notice asked for the picker. It goes through the
  // same gate as the composer's own button. When the picker opens, the picker
  // consumes the request to pick its filter; when the gate redirects instead,
  // nothing will, so the request is dropped here. It waits for the catalog:
  // before it hydrates, "no approved template" only means "not loaded yet".
  useEffect(() => {
    if (!templatePickerRequest) return;
    // RET-2339 (D6): a read-only chat cannot send a template either. The
    // request is dropped instead of opening the picker or redirecting to the
    // Plantillas tab.
    if (isWapiPlanReadOnly) {
      setTemplatePickerRequest(null);
      return;
    }
    if (!templatesHydrated) return;
    handleClickOnOpeningTemplate();
    if (!hasApprovedTemplate) setTemplatePickerRequest(null);
  }, [templatePickerRequest, templatesHydrated]);

  // AGI-500: Lead Agent's indicator offers "Tomar conversación" once Lead Agent
  // has not replied in 45 s. The take-over is the one this composer owns: switch
  // Lead Agent off for the chat, then the 'tomar_control' survey. It only ever
  // switches Lead Agent off: when it already is, the conversation is already
  // the person's and the composer is open.
  useEffect(() => {
    if (!takeConversationRequest) return;
    setTakeConversationRequest(null);
    if (takeConversationRequest.chatId !== selectedChat?.id) return;
    if (aiAutoRespondEnabled) onAutoRespondToggle('tomar_control');
  }, [takeConversationRequest]);

  const renderWapiBannerMessage = () => {
    if (whatsappBusinessIntegration.state !== eIntegrationStatus.CONNECTED) {
      if (currentUserCanReconnectWabi) {
        return (
          <>
            <strong>{t('wapiBanner.disconnected.admin')}</strong>{' '}
            <button
              className="underline cursor-pointer"
              onClick={() => setWhatsAppBusinessConnectionModalIsOpen(true)}
            >
              {t('wapiBanner.disconnected.adminLink')}
            </button>
          </>
        );
      }
      return <strong>{t('wapiBanner.disconnected.seller')}</strong>;
    }
    if (hasApprovedTemplate) {
      // RET-2254: this copy is a claim about MONEY (and, since v2, about the
      // free entry point), so it's resolved to a literal key here — never by
      // composing strings — before the return, instead of a ternary nested in
      // the JSX. Order matches the frozen table:
      //   cohort not yet resolved                          -> textNoPrice
      //   wallet cohort + free entry point open             -> textNoPrice
      //     (Meta's ad-click window: EVERY template is free at source while it's
      //     open — isTemplateFreeAtSource, same signal the template list's
      //     "Gratis" badge reads via selectedChat?.freeEntryPointData
      //     ?.hasFreeEntryPoint — so "it's deducted from your balance" would
      //     be false)
      //   wallet cohort, entry point closed (or absent)     -> textWallet
      //     (RET-2339: only with `isWalletSurfaceVisible`; otherwise
      //     textNoPrice)
      //   credits cohort (wallet off)                       -> text (unchanged)
      let approvedTemplateBannerTextKey: string;
      if (!hasLoadedWalletFlag) {
        approvedTemplateBannerTextKey = 'wapiBanner.approved.textNoPrice';
      } else if (
        isWalletEnabled &&
        selectedChat?.freeEntryPointData?.hasFreeEntryPoint
      ) {
        approvedTemplateBannerTextKey = 'wapiBanner.approved.textNoPrice';
      } else if (isWalletEnabled && isWalletSurfaceVisible) {
        approvedTemplateBannerTextKey = 'wapiBanner.approved.textWallet';
      } else if (isWalletEnabled) {
        // RET-2339 U4: wallet cohort, but no WhatsApp API access confirmed
        // (plan unresolved, or a plan without it): no claim about the balance.
        approvedTemplateBannerTextKey = 'wapiBanner.approved.textNoPrice';
      } else {
        approvedTemplateBannerTextKey = 'wapiBanner.approved.text';
      }
      return (
        <>
          <strong>{t('wapiBanner.approved.title')}</strong>{' '}
          {t(approvedTemplateBannerTextKey)}
        </>
      );
    }
    if (hasAnyTemplate) {
      return <strong>{t('wapiBanner.pendingApproval.title')}</strong>;
    }
    return <strong>{t('wapiBanner.noTemplates.title')}</strong>;
  };

  const renderTemplateButton = () => {
    if (whatsappBusinessIntegration.state !== eIntegrationStatus.CONNECTED) {
      return null;
    }
    if (hasApprovedTemplate) {
      return (
        <Tooltip
          className="col-span-3"
          message={`${t('wapiBanner.button.tooltip.enable')}`}
        >
          <Button
            onClick={handleClickOnOpeningTemplate}
            className="MessageBar__BannerWapi__Button"
          >
            {t('wapiBanner.btn')}
          </Button>
        </Tooltip>
      );
    }
    return (
      <Button
        onClick={handleClickOnOpeningTemplate}
        className="MessageBar__BannerWapi__Button"
      >
        {hasAnyTemplate
          ? t('wapiBanner.pendingApproval.btn')
          : t('wapiBanner.noTemplates.btn')}
      </Button>
    );
  };

  return (
    <div className="MessageBar">
      {handoffReasonModalProps.open && (
        <HandoffReasonModal {...handoffReasonModalProps} />
      )}
      {/* INT-1500 U12: renders nothing while its flag is off. */}
      <UpcomingSendsBar />
      <div className="relative">
        {replyMessage && shouldRenderNewInputContainer && !isWapiPlanReadOnly ? (
          <ReplyAction />
        ) : null}
        {!isInPlanPauseWindow &&
        (!isWapiPlanReadOnly || isActionAllowedInReadOnlyChat) ? (
          <RenderActionComponent />
        ) : null}

        <AllowTypesBanner />
        {renderDisconnectedBanner()}

        {showLeadAgentPausedBanner && (
          <div
            role="alert"
            data-testid="lead-agent-conversation-paused-banner"
            className="mx-3 my-2 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-500"
          >
            <PauseIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="flex flex-1 flex-col text-xs">
              {isPurchaseProcessing ? (
                <span>
                  {t('smartLeadbot.banners.billingPaused.processing')}
                </span>
              ) : (
                <>
                  <span>{t(billingPauseCopyKeys.body)}</span>
                  {trialCapEndDate && (
                    <span>
                      {t('smartLeadbot.banners.billingPaused.trialCapEndsOn', {
                        date: trialCapEndDate,
                      })}
                    </span>
                  )}
                  {(!currentUserCanChooseLeadAgentPlan ||
                    billingPauseCopyKeys.cta === null) &&
                    billingPauseCopyKeys.sellerHint && (
                      <span>{t(billingPauseCopyKeys.sellerHint)}</span>
                    )}
                </>
              )}
            </span>
            {currentUserCanChooseLeadAgentPlan &&
              billingPauseCopyKeys.cta !== null &&
              !isPurchaseProcessing && (
                <DesignSystemButton
                  size="sm"
                  onClick={handleBillingPauseCta}
                  loading={isBillingPauseCtaLoading}
                  className="shrink-0"
                >
                  {t(billingPauseCopyKeys.cta)}
                </DesignSystemButton>
              )}
            {canDismissPausedBanner && (
              <DesignSystemButton
                color="ghost"
                size="sm"
                iconOnly
                aria-label={t('smartLeadbot.banners.billingPaused.dismiss')}
                data-testid="lead-agent-conversation-paused-banner-dismiss"
                onClick={handleDismissPausedBanner}
                className="shrink-0"
              >
                <XMarkIcon aria-hidden="true" className="h-4 w-4" />
              </DesignSystemButton>
            )}
          </div>
        )}
        {isReactivateLeadAgentOpen && (
          <ReactivateLeadAgentModal
            onClose={() => setIsReactivateLeadAgentOpen(false)}
            onReactivated={handleLeadAgentReactivated}
          />
        )}
        {isUpgradeTierOpen && (
          <UpgradeTierModal
            workspaceId={currentWorkspaceId ?? ''}
            currentTier={usageTier}
            currentCapacityPct={usageCapacityPct}
            capacityResetDate={usageResetDate}
            onClose={() => setIsUpgradeTierOpen(false)}
            onUpgraded={() => {
              setIsUpgradeTierOpen(false);
              setIsPurchaseProcessing(true);
            }}
          />
        )}
        {actionModals}
        {/* AGI-766: only the Owner opens it. Mounted while the Owner is known so the
            DS Modal can animate out after a close. */}
        {currentUserIsOwner && currentWorkspaceId && (
          <LeadAgentPauseDismissDialog
            open={isDismissSurveyOpen}
            workspaceId={currentWorkspaceId}
            pauseReason={leadAgentPausedReason}
            everUsed={everUsed ?? null}
            onClose={() => setIsDismissSurveyOpen(false)}
            onDismissed={() => {
              setIsDismissSurveyOpen(false);
              leadAgentChatBanner.markDismissed();
            }}
          />
        )}

        {slbBannerInfo && (
          <div
            className={`flex flex-col gap-0.5 px-4 py-2 border-l-4 ${
              slbBannerInfo.type === 'warning'
                ? 'bg-amber-50 border-amber-400 text-amber-800'
                : slbBannerInfo.type === 'escalation'
                  ? 'bg-violet-50 border-violet-400 text-violet-800'
                  : 'bg-blue-50 border-blue-400 text-blue-800'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="flex-1 text-xs">{slbBannerInfo.message}</span>
              {/* RET-870 — Atendido is offered whenever the current chat's lead is in
                  the PENDIENTE set (escalation, expired, or non-manual deactivation
                  all land there). It does NOT fire for gate_failed banners since
                  those don't add the lead to the escalated set. Acknowledgment
                  keeps the kanban sort pinning, only suppresses the chip. */}
              {selectedChat?.leadId &&
                slbEscalatedLeadIds.has(selectedChat.leadId) &&
                !slbAcknowledgedLeadIds.has(selectedChat.leadId) && (
                  <button
                    onClick={() => {
                      if (selectedChat?.id)
                        slbBannerDismissedChats.add(selectedChat.id);
                      if (selectedChat?.leadId)
                        acknowledgeSlbEscalatedLead(selectedChat.leadId);
                      setSlbBannerInfo(null);
                    }}
                    className={`shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-semibold active:scale-95 transition-all ${
                      slbBannerInfo.type === 'escalation'
                        ? 'bg-violet-500 text-white hover:bg-violet-600'
                        : slbBannerInfo.type === 'warning'
                          ? 'bg-amber-500 text-white hover:bg-amber-600'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {t('smartLeadbot.banners.escalation.markAttended')}
                  </button>
                )}
              <button
                onClick={() => {
                  if (selectedChat?.id)
                    slbBannerDismissedChats.add(selectedChat.id);
                  setSlbBannerInfo(null);
                }}
                className="shrink-0 opacity-50 hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            {slbBannerInfo.detail && (
              <span className="text-[11px] italic opacity-70">
                {slbBannerInfo.detail}
              </span>
            )}
          </div>
        )}
      </div>
      {isInPlanPauseWindow ? (
        <div className="MessageBar__Textarea">
          <div className="MessageBar__Container">
            <div
              role="status"
              data-testid="plan-pause-composer"
              className="flex w-full flex-col gap-2 rounded-lg bg-base-200 p-4"
            >
              <p className="font-semibold">{t('planPauseComposer.title')}</p>
              <p className="text-sm text-base-content/70">
                {t('planPauseComposer.body', {
                  // The same UTC boundary the subscription card shows: in the
                  // browser's zone it read as the day before west of Greenwich.
                  endsAt: planPause?.endsAt
                    ? formatPlanPauseDate({
                        instant: planPause.endsAt,
                        locale: i18n.language,
                        style: 'long',
                      })
                    : '',
                })}
              </p>
              {currentUserIsOwner ? (
                <Button
                  size="sm"
                  className="self-start"
                  onClick={() =>
                    navigate(
                      `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}`,
                    )
                  }
                >
                  {t('planPauseComposer.ownerCta')}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : isWapiPlanReadOnly ? (
        // RET-2339 (D5): a top-level branch, like the plan pause above, so it
        // does not depend on `shouldRenderNewInputContainer` (false with
        // NOTES, PROGRAMMED_MESSAGES and CREATE_QUICK_ACTION) and the bar is
        // never left empty. It wins over the blocked-contact banner:
        // unblocking the contact would not let this workspace reply.
        <div className="MessageBar__Textarea">
          <div className="MessageBar__Container">
            <WapiPlanReadOnlyBanner
              canUpgradePlan={currentUserIsOwnerOrAdmin}
              onUpgradePlan={handleWapiPlanUpgrade}
              canReplyByWhatsAppWeb={canReplyWapiPlanChatByWeb}
              onReplyByWhatsAppWeb={handleWapiPlanReplyByWeb}
              webSwitchFailed={isWapiPlanWebSwitchFailed}
            />
          </div>
        </div>
      ) : shouldRenderNewInputContainer ? (
        <div className="MessageBar__Textarea">
          <div className="MessageBar__Container">
            {/* INT-941 / U2 — the read-only branch sits ABOVE the priority
                ternary AND co-suppresses the EmojiAction / PlusMenu sibling
                block. When read-only, the input, toolbar, attachments,
                emoji/plus and send are all unmounted (not disabled) so
                suppressed controls leave the tab order.
                INT-1294 — the blocked-contact branch takes top priority for
                the same reason: every send path (text, templates, quick
                replies) is a guaranteed delivery failure while blocked. */}
            {isContactBlocked ? (
              <BlockedContactBanner
                loading={unblockLoading}
                onUnblock={() => {
                  void setContactBlocked(false);
                }}
              />
            ) : isWhatsAppWebReadOnly ? (
              <WhatsAppWebReadOnlyBanner
                hasWaba={hasWaBusinessIntegration()}
                showRetry={showWhatsAppWebRetry}
                onRetry={whatsAppWebConnection.refetch}
                onSwitchToWhatsAppBusiness={switchToWhatsAppBusiness}
              />
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 w-16 mb-[6px]">
                  <EmojiAction />
                  <PlusMenu />
                </div>
                {isLeadbotReActivated ? (
                  <div className="MessageBar__LeadbotBanner">
                    <LeadbotMsgIcon />
                    En este momento, el lead está siendo atendido por el
                    Leadbot.
                    <br />
                    Podrás retomar la conversación una vez que finalice el
                    flujo.
                  </div>
                ) : inputBlockError ? (
                  <div className="flex flex-col w-full px-4 py-3">
                    <div className="bg-error/10 border-l-4 border-error text-error-content px-4 py-3 rounded-r">
                      <p className="font-bold text-sm">
                        {t(inputBlockError.titleKey, { ns: 'errors/errors' })}
                      </p>
                      <p className="text-xs mt-1">
                        {t(inputBlockError.messageKey, { ns: 'errors/errors' })}
                      </p>
                    </div>
                  </div>
                ) : shouldShowChatInput ? (
                  <>
                    <div className="flex flex-grow flex-col min-w-0 relative">
                      {slbDirectorOpen &&
                        selectedChat?.id &&
                        currentWorkspaceId && (
                          <SLBDirectorPanel
                            chatId={selectedChat.id}
                            workspaceId={currentWorkspaceId}
                            onClose={() => setSlbDirectorOpen(false)}
                          />
                        )}
                      {isSmartLeadBotEnabled &&
                      isSLBChat &&
                      aiAutoRespondEnabled &&
                      !slbOverrideWrite &&
                      !leadAgentPaused ? (
                        <div className="MessageBar__SLBBanner">
                          <div className="MessageBar__SLBBanner__StatusRow">
                            <SignalIcon className="shrink-0 w-3.5 h-3.5 text-violet-500 animate-pulse" />
                            <span className="flex-1 text-base-content/50">
                              Lead Agent activo
                            </span>
                            {/* EXPERIMENT: per-chat actions (intervene/guide) moved
                            into the AIResponsePreview card — banner is status-only.
                            The card's toggle covers deactivation, so the
                            'tomar_control' survey path is bypassed here. */}
                          </div>
                        </div>
                      ) : isSmartLeadBotEnabled &&
                        isSLBChat &&
                        aiAutoRespondEnabled &&
                        slbOverrideWrite &&
                        !leadAgentPaused ? (
                        <>
                          <div className="MessageBar__SLBBanner MessageBar__SLBBanner--paused">
                            <div className="MessageBar__SLBBanner__StatusRow">
                              <span className="shrink-0 w-2 h-2 rounded-full bg-base-content/30" />
                              <span className="flex-1 text-base-content/60 text-[11px]">
                                {t('smartLeadbot.slbBanner.paused')}
                              </span>
                              <button
                                onClick={() => setSlbOverrideWrite(false)}
                                className="shrink-0 px-2.5 py-0.5 rounded-full border border-base-300 text-[10px] font-medium text-base-content/60 hover:bg-base-200 active:scale-95 transition-all"
                              >
                                {t('smartLeadbot.slbBanner.interveningCancel')}
                              </button>
                            </div>
                          </div>
                          {selectedAction ===
                          ChatEnum.WABTEMPLATES_VARIABLES ? null : (
                            <TextAreaContainer
                              seal={visibleComposerSeal}
                              sealTooltip={composerSealTooltip}
                              isBlocked={isComposerSealBlocked}
                              costLabel={composerCostLabel}
                              shortfall={composerShortfall}
                            />
                          )}
                        </>
                      ) : isSmartLeadBotEnabled &&
                        isSLBChat &&
                        !aiAutoRespondEnabled &&
                        !autoRespondDisabledReason ? (
                        <>
                          {/* Agent toggled off by the user: the lead is unattended
                          until someone replies or reactivates the agent. */}
                          <div className="MessageBar__SLBBanner MessageBar__SLBBanner--paused">
                            <div className="MessageBar__SLBBanner__StatusRow">
                              <span className="shrink-0 w-2 h-2 rounded-full bg-base-content/30" />
                              <span className="flex-1 text-base-content/60 text-[11px]">
                                {t('smartLeadbot.slbBanner.paused')}
                              </span>
                            </div>
                          </div>
                          {selectedAction ===
                          ChatEnum.WABTEMPLATES_VARIABLES ? null : (
                            <TextAreaContainer
                              seal={visibleComposerSeal}
                              sealTooltip={composerSealTooltip}
                              isBlocked={isComposerSealBlocked}
                              costLabel={composerCostLabel}
                              shortfall={composerShortfall}
                            />
                          )}
                        </>
                      ) : selectedAction ===
                        ChatEnum.WABTEMPLATES_VARIABLES ? null : (
                        <TextAreaContainer
                          seal={visibleComposerSeal}
                          sealTooltip={composerSealTooltip}
                          isBlocked={isComposerSealBlocked}
                          costLabel={composerCostLabel}
                          shortfall={composerShortfall}
                        />
                      )}

                      <Assets />
                    </div>
                    <ToolActions
                      autoRespondEnabled={aiAutoRespondEnabled}
                      autoRespondSaving={autoRespondSaving}
                      // RET-1069: ToolActions forwards to AIResponsePreview's
                      // switch. That switch deactivates the bot for THIS chat
                      // (not the workspace) — route the mandatory nested
                      // survey ('apagar_bot_chat', 30min cooldown).
                      onAutoRespondToggle={() =>
                        onAutoRespondToggle('apagar_bot_chat')
                      }
                      onIntervene={() => {
                        setSlbOverrideWrite(true);
                        setSlbDirectorOpen(false);
                      }}
                      onGuide={() => setSlbDirectorOpen((o) => !o)}
                      autoRespondDisabledReason={autoRespondDisabledReason}
                    />
                  </>
                ) : (
                  <>
                    {tiktokInactive ? (
                      <div className="MessageBar__InactiveBanner">
                        {t('tiktokBanner.inactive')}
                      </div>
                    ) : selectedChat?.state === ChatState.EXPIRED ||
                      canSendTemplate24Hrs ||
                      cantSendTemplate24Hrs ? (
                      <div className="MessageBar__InactiveBanner">
                        {getInactiveBannerText()}
                      </div>
                    ) : whatsAppAPIInactive &&
                      !hasWaBusinessIntegration() &&
                      whatsappIntegration.state ===
                        eIntegrationStatus.CONNECTED ? (
                      <div className="MessageBar__BannerWapi">
                        <p className="MessageBar__BannerWapi__Text">
                          <strong>{t('wapiBanner.migratedToWeb.title')}</strong>{' '}
                          {t('wapiBanner.migratedToWeb.text')}
                        </p>
                        <Button
                          onClick={switchToWhatsAppWeb}
                          className="MessageBar__BannerWapi__Button"
                        >
                          {t('wapiBanner.migratedToWeb.btn')}
                        </Button>
                      </div>
                    ) : hasWaBusinessIntegration() &&
                      whatsAppAPIInactive &&
                      templatesHydrated ? (
                      <div className="MessageBar__BannerWapi">
                        <p className="MessageBar__BannerWapi__Text">
                          {renderWapiBannerMessage()}
                        </p>
                        {renderTemplateButton()}
                      </div>
                    ) : null}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
